import { useId, useState } from 'react';
import { InputNumber } from 'antd';
import { ApiOutlined, CheckCircleOutlined, DeleteOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';

import { Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Badge, Button, Field, Input, InputPassword, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { QUYEN, TRANG_THAI_KET_NOI } from '../../constants';
import { useKetNoi, useKiemKetNoi, useLuuKetNoi } from '../../hooks/useKetNoi';
import { KHOANG, banNhapTu, kiemKetNoi, thanGuiKetNoi } from '../../nghiep-vu/ket-noi';
import type { BanNhapKetNoi, LoiKetNoi, TruongKetNoi } from '../../nghiep-vu/ket-noi';
import type { KetNoi } from '../../types';
import { DongBatTat, LoiRpa } from '../chung';

/**
 * Tab "Kết nối OpenCore" (mới, không có ở native; ADR-0103 mục 5): địa chỉ, địa chỉ gửi lệnh, JWT dịch vụ (chỉ ghi)
 * và các tuỳ chọn nâng cao gập lại. Đi qua `key` theo `updatedAt` để bản nháp dựng lại từ đúng bản máy chủ vừa trả.
 */
export function TabKetNoi({ suaDuoc }: { suaDuoc: boolean }) {
  const q = useKetNoi();
  // Hai lệnh ghi sống ở đây, không ở form: lưu xong thì form dựng lại theo `updatedAt` mới, callback của `mutate`
  // gắn với form cũ sẽ không chạy.
  const luu = useLuuKetNoi();
  const go = useLuuKetNoi();

  if (q.isPending) {
    return (
      <Tile>
        <div className="space-y-3 p-5" aria-busy="true">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      </Tile>
    );
  }
  if (q.isError) {
    return (
      <Tile>
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      </Tile>
    );
  }
  return <FormKetNoi key={q.data.configured ? q.data.updatedAt : 'chua-khai'} goc={q.data} suaDuoc={suaDuoc} luu={luu} go={go} />;
}

/** Khoá câu lỗi của từng loại lỗi ở từng trường. */
function cauLoi(
  truong: TruongKetNoi,
  ma: NonNullable<LoiKetNoi[TruongKetNoi]>,
): { khoa: KhoaDich; khoang?: { min: number; max: number } } {
  if (ma === 'batBuoc') return { khoa: 'rpaKetNoi.loiBatBuoc' };
  if (ma === 'canJwt') return { khoa: 'rpaKetNoi.loiCanJwt' };
  if (ma === 'saiKhoang' && truong in KHOANG) {
    const [min, max] = KHOANG[truong as keyof typeof KHOANG];
    return { khoa: 'rpaKetNoi.loiKhoang', khoang: { min, max } };
  }
  if (truong === 'url' || truong === 'sdkUrl') return { khoa: 'rpaKetNoi.loiUrl' };
  if (truong === 'jwt') return { khoa: 'rpaKetNoi.loiJwtDai' };
  if (truong === 'workflowsCollection' || truong === 'robotsCollection') return { khoa: 'rpaKetNoi.loiCollection' };
  return { khoa: 'rpaKetNoi.loiRole' };
}

type LenhLuu = ReturnType<typeof useLuuKetNoi>;

function FormKetNoi({ goc, suaDuoc, luu, go }: { goc: KetNoi; suaDuoc: boolean; luu: LenhLuu; go: LenhLuu }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const kiem = useKiemKetNoi();
  const [d, setD] = useState<BanNhapKetNoi>(() => banNhapTu(goc));
  const [daGui, setDaGui] = useState(false);
  const [moNangCao, setMoNangCao] = useState(false);
  const [hoiGoJwt, setHoiGoJwt] = useState(false);

  const than = thanGuiKetNoi(d, goc);
  const coThayDoi = Object.keys(than).length > 0;
  const loi = daGui ? kiemKetNoi(d, goc) : {};
  const truongMay = loiTheoTruong(luu.error);
  const loiChung = luu.error && Object.keys(truongMay).length === 0 ? dichLoi(luu.error) : null;

  const loiCua = (k: TruongKetNoi): string | undefined => {
    const ma = loi[k];
    if (ma) {
      const c = cauLoi(k, ma);
      return c.khoang ? t(c.khoa, { min: soVN(c.khoang.min, locale), max: soVN(c.khoang.max, locale) }) : t(c.khoa);
    }
    return truongMay[`/${k}`];
  };

  const dat = <K extends keyof BanNhapKetNoi>(k: K, v: BanNhapKetNoi[K]) => {
    setD((cu) => ({ ...cu, [k]: v }));
    luu.reset();
    kiem.reset();
  };

  const gui = () => {
    setDaGui(true);
    if (Object.keys(kiemKetNoi(d, goc)).length > 0) {
      // Lỗi nằm ở phần nâng cao đang gập: mở ra, không thì người dùng không thấy ô nào sai.
      const loiNangCao = Object.keys(kiemKetNoi(d, goc)).some((k) => !['url', 'sdkUrl', 'jwt'].includes(k));
      if (loiNangCao) setMoNangCao(true);
      return;
    }
    if (!coThayDoi || luu.isPending) return;
    luu.mutate(than, { onSuccess: () => toast.success(t('rpaKetNoi.daLuu')) });
  };

  const trangThai = TRANG_THAI_KET_NOI[!goc.configured ? 'chuaKhai' : goc.enabled ? 'dangBat' : 'dangTat'];
  const chiDoc = !suaDuoc;

  const oSo = (k: keyof typeof KHOANG, nhan: KhoaDich, goiY: KhoaDich) => (
    <Field label={t(nhan)} hint={t(goiY)} error={loiCua(k)}>
      {(p) => (
        <InputNumber
          {...p}
          className="w-full"
          status={p['aria-invalid'] ? 'error' : undefined}
          disabled={chiDoc}
          min={KHOANG[k][0]}
          max={KHOANG[k][1]}
          precision={0}
          value={d[k]}
          onChange={(v) => dat(k, v)}
        />
      )}
    </Field>
  );

  const oChu = (k: 'workflowsCollection' | 'robotsCollection' | 'licenseRole' | 'projectRolePrefix', nhan: KhoaDich, goiY: KhoaDich) => (
    <Field label={t(nhan)} hint={t(goiY)} error={loiCua(k)}>
      {(p) => <Input {...p} className="font-mono" disabled={chiDoc} value={d[k]} onChange={(e) => dat(k, e.target.value)} />}
    </Field>
  );

  return (
    <Tile>
      <TileHeader
        icon={<ApiOutlined />}
        tone="primary"
        title={t('rpaKetNoi.tieuDe')}
        mo={t('rpaKetNoi.moTa')}
        action={<Badge variant={trangThai.variant} className="whitespace-nowrap">{t(trangThai.khoa)}</Badge>}
      />
      <TileBody className="space-y-5">
        {!goc.configured && <p className="text-sm text-content-secondary">{t('rpaKetNoi.chuaKhaiMoTa')}</p>}
        {goc.configured && (
          <p className="text-xs text-content-muted">{t('rpaKetNoi.capNhatLuc', { luc: ngayGioVN(goc.updatedAt, locale) })}</p>
        )}
        {chiDoc && <p className="text-xs text-content-muted">{t('rpaKetNoi.chiXem', { quyen: QUYEN.ketNoiSua })}</p>}

        <form
          id={formId}
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            gui();
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={t('rpaKetNoi.url')} required hint={t('rpaKetNoi.urlGoiY')} error={loiCua('url')}>
              {(p) => (
                <Input
                  {...p}
                  className="font-mono"
                  disabled={chiDoc}
                  maxLength={500}
                  inputMode="url"
                  autoComplete="off"
                  value={d.url}
                  placeholder={t('rpaKetNoi.urlMau')}
                  onChange={(e) => dat('url', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('rpaKetNoi.sdkUrl')} hint={t('rpaKetNoi.sdkUrlGoiY')} error={loiCua('sdkUrl')}>
              {(p) => (
                <Input
                  {...p}
                  className="font-mono"
                  disabled={chiDoc}
                  maxLength={500}
                  inputMode="url"
                  autoComplete="off"
                  value={d.sdkUrl}
                  onChange={(e) => dat('sdkUrl', e.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label={t('rpaKetNoi.jwt')} required={!(goc.configured && goc.hasJwt)} hint={t('rpaKetNoi.jwtGoiY')} error={loiCua('jwt')}>
            {(p) => (
              <div className="space-y-2">
                <p className="flex flex-wrap items-center gap-2 text-xs">
                  {goc.configured && goc.hasJwt ? (
                    <Badge variant="success">
                      <CheckCircleOutlined aria-hidden />
                      {t('rpaKetNoi.jwtDaLuu')}
                    </Badge>
                  ) : (
                    <Badge variant="warning">{t('rpaKetNoi.jwtChuaCo')}</Badge>
                  )}
                  {suaDuoc && goc.configured && goc.hasJwt && (
                    <Button variant="link" size="sm" danger icon={<DeleteOutlined />} onClick={() => setHoiGoJwt(true)}>
                      {t('rpaKetNoi.goJwt')}
                    </Button>
                  )}
                </p>
                {suaDuoc && (
                  <InputPassword
                    {...p}
                    className="font-mono"
                    autoComplete="new-password"
                    value={d.jwt}
                    placeholder={t(goc.configured && goc.hasJwt ? 'rpaKetNoi.jwtGiuNguyen' : 'rpaKetNoi.jwtNhap')}
                    onChange={(e) => dat('jwt', e.target.value)}
                  />
                )}
              </div>
            )}
          </Field>

          <section className="rounded-lg border border-default">
            <button
              type="button"
              aria-expanded={moNangCao}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left hover:bg-subtle"
              onClick={() => setMoNangCao((v) => !v)}
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-content-primary">{t('rpaKetNoi.nangCao')}</span>
                <span className="block text-xs text-content-muted">{t('rpaKetNoi.nangCaoMoTa')}</span>
              </span>
              {moNangCao ? <DownOutlined aria-hidden /> : <RightOutlined aria-hidden />}
            </button>
            {moNangCao && (
              <div className="grid grid-cols-1 gap-4 border-t border-default p-4 md:grid-cols-2">
                {oChu('workflowsCollection', 'rpaKetNoi.workflowsCollection', 'rpaKetNoi.collectionGoiY')}
                {oChu('robotsCollection', 'rpaKetNoi.robotsCollection', 'rpaKetNoi.collectionGoiY')}
                {oChu('licenseRole', 'rpaKetNoi.licenseRole', 'rpaKetNoi.licenseRoleGoiY')}
                {oChu('projectRolePrefix', 'rpaKetNoi.projectRolePrefix', 'rpaKetNoi.projectRolePrefixGoiY')}
                {oSo('projectWorkflowRights', 'rpaKetNoi.projectWorkflowRights', 'rpaKetNoi.quyenGoiY')}
                {oSo('projectProRights', 'rpaKetNoi.projectProRights', 'rpaKetNoi.quyenGoiY')}
                {oSo('rpcTimeoutSeconds', 'rpaKetNoi.rpcTimeoutSeconds', 'rpaKetNoi.rpcTimeoutGoiY')}
                {oSo('maxRetry', 'rpaKetNoi.maxRetry', 'rpaKetNoi.maxRetryGoiY')}
                {oSo('licenseSyncIntervalMinutes', 'rpaKetNoi.licenseSyncIntervalMinutes', 'rpaKetNoi.licenseSyncGoiY')}
                <div className="md:col-span-2">
                  <DongBatTat
                    nhan={t('rpaKetNoi.enabled')}
                    goiY={t('rpaKetNoi.enabledGoiY')}
                    bat={d.enabled}
                    voHieu={chiDoc}
                    onDoi={(v) => dat('enabled', v)}
                  />
                </div>
              </div>
            )}
          </section>
        </form>

        {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}

        <KetQuaKiem kiem={kiem} />

        <div className="flex flex-wrap items-center justify-end gap-3">
          {goc.configured && (
            <Button
              variant="outline"
              size="md"
              icon={<ApiOutlined />}
              loading={kiem.isPending}
              disabled={coThayDoi}
              title={coThayDoi ? t('rpaKetNoi.kiemTruocLuu') : undefined}
              onClick={() => kiem.mutate()}
            >
              {kiem.isPending ? t('rpaKetNoi.dangKiem') : t('rpaKetNoi.kiemTra')}
            </Button>
          )}
          {suaDuoc && (
            <>
              <Button
                variant="outline"
                size="md"
                disabled={!coThayDoi || luu.isPending}
                onClick={() => {
                  setD(banNhapTu(goc));
                  setDaGui(false);
                  luu.reset();
                }}
              >
                {t('rpaKetNoi.hoanTac')}
              </Button>
              <Button variant="primary" size="md" htmlType="submit" form={formId} loading={luu.isPending} disabled={!coThayDoi}>
                {luu.isPending ? t('chung.dangLuu') : t('rpaKetNoi.luu')}
              </Button>
            </>
          )}
        </div>
      </TileBody>

      {hoiGoJwt && (
        <HopXacNhanThaoTac
          open
          nguyHiem
          tieuDe={t('rpaKetNoi.goJwtTieuDe')}
          than={t('rpaKetNoi.goJwtThan')}
          nhanXacNhan={t('rpaKetNoi.goJwt')}
          dangChay={go.isPending}
          loi={go.error ? dichLoi(go.error) : null}
          onClose={() => {
            go.reset();
            setHoiGoJwt(false);
          }}
          onXacNhan={() =>
            go.mutate({ jwt: null }, {
              onSuccess: () => {
                toast.success(t('rpaKetNoi.daGoJwt'));
                setHoiGoJwt(false);
              },
            })
          }
        />
      )}
    </Tile>
  );
}

/** Kết quả "Kiểm tra kết nối": dòng xác nhận khi được, câu lỗi của máy chủ (nguyên văn) khi không. */
function KetQuaKiem({ kiem }: { kiem: ReturnType<typeof useKiemKetNoi> }) {
  const { t } = useT();
  const dichLoi = useLoi();
  if (kiem.isSuccess) {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-success-700">
        <CheckCircleOutlined aria-hidden />
        {t('rpaKetNoi.kiemDuoc', { url: kiem.data.openiapUrl })}
      </p>
    );
  }
  if (kiem.isError) return <LoiTrongHop>{dichLoi(kiem.error)}</LoiTrongHop>;
  return null;
}
